const { useCallback } = require('react')
const moment = require('moment-timezone')
const { useAddressMap } = require('./addressMap.hook')

const useFormatBytes = () => {
    const { getAddressName } = useAddressMap()

    return useCallback((data, length = 15, first = 8, end = 5) => {
        const resolved = getAddressName(data) || data
        if (!resolved) return ''
        if (resolved.length <= length) return resolved
        return resolved.slice(0, first) + '...' + (end > 0 ? resolved.slice(-end) : '')
    }, [])
}

const useFormatDateTime = () => {
    return useCallback(timestamp => {
        const date = new Date(timestamp * 1000)
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')
        const hours = String(date.getHours()).padStart(2, '0')
        const minutes = String(date.getMinutes()).padStart(2, '0')
        const seconds = String(date.getSeconds()).padStart(2, '0')

        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
    }, [])
}

const useFormatTimestampShort = () => {
    return useCallback(timestamp => {
        const date = moment.unix(timestamp).utc()
        const now = moment.utc()
        const duration = moment.duration(now.diff(date))

        let relativeTime
        if (duration.asSeconds() < 60) {
            const seconds = Math.floor(duration.asSeconds())
            relativeTime = seconds === 1 ? '1 sec ago' : `${seconds} secs ago`
        } else if (duration.asMinutes() < 60) {
            const minutes = Math.floor(duration.asMinutes())
            relativeTime = minutes === 1 ? '1 min ago' : `${minutes} mins ago`
        } else if (duration.asHours() < 24) {
            const hours = Math.floor(duration.asHours())
            relativeTime = hours === 1 ? '1 hr ago' : `${hours} hrs ago`
        } else {
            const days = Math.floor(duration.asDays())
            relativeTime = days === 1 ? '1 day ago' : `${days} days ago`
        }

        return relativeTime
    }, [])
}

const useFormatTimestamp = () => {
    return useCallback(timestamp => {
        const date = moment.unix(timestamp).utc()
        const now = moment.utc()
        const duration = moment.duration(now.diff(date))

        let relativeTime
        if (duration.asSeconds() < 60) {
            const seconds = Math.floor(duration.asSeconds())
            relativeTime = seconds === 1 ? '1 sec ago' : `${seconds} secs ago`
        } else if (duration.asMinutes() < 60) {
            const minutes = Math.floor(duration.asMinutes())
            relativeTime = minutes === 1 ? '1 min ago' : `${minutes} mins ago`
        } else if (duration.asHours() < 24) {
            const hours = Math.floor(duration.asHours())
            relativeTime = hours === 1 ? '1 hr ago' : `${hours} hrs ago`
        } else {
            const days = Math.floor(duration.asDays())
            relativeTime = days === 1 ? '1 day ago' : `${days} days ago`
        }

        const formattedDate = date.format('MMM-DD-YYYY hh:mm:ss A [+UTC]')

        return `${relativeTime} (${formattedDate})`
    }, [])
}

const useFormatTransactionCount = () => {
    return useCallback(count => {
        if (!count) return '0 txn'
        const num = parseInt(count)
        return `${num} txn${num === 1 ? '' : 's'}`
    }, [])
}

const useHexToString = () => {
    return useCallback(hex => {
        if (!hex) return ''
        hex = hex.startsWith('0x') ? hex.slice(2) : hex

        try {
            const bytes = new Uint8Array(hex.match(/.{1,2}/g).map(byte => parseInt(byte, 16)))
            return new TextDecoder().decode(bytes).replace(/\x00/g, '')
        } catch (error) {
            return ''
        }
    }, [])
}

const useFormatNumber = () => {
    return useCallback(num => {
        return num.toString().padStart(2, '0')
    }, [])
}

const useFormatHexValue = () => {
    const { getAddressName } = useAddressMap()

    return useCallback(
        (value, type = null) => {
            if (!value) return value
            if (!type) return value

            try {
                switch (type.toLowerCase()) {
                    case 'address':
                        return '0x' + value.slice(26)

                    case 'uint256':
                    case 'uint160':
                    case 'uint128':
                    case 'uint64':
                    case 'uint32':
                    case 'uint24':
                    case 'uint16':
                    case 'uint8':
                        return BigInt(value).toString()
                    case 'int256':
                    case 'int160':
                    case 'int128':
                    case 'int64':
                    case 'int32':
                    case 'int24':
                    case 'int16':
                    case 'int8': {
                        const bits = parseInt(type.replace('int', ''), 10)
                        const hexLength = bits / 4
                        const bigIntValue = BigInt(`0x${value.slice(-hexLength)}`)
                        const maxPositive = BigInt(2 ** (bits - 1) - 1)

                        if (bigIntValue > maxPositive) {
                            const actualValue = bigIntValue - BigInt(2 ** bits)
                            return actualValue.toString()
                        }
                        return bigIntValue.toString()
                    }
                    case 'string':
                        // Remove '0x' prefix if exists and decode
                        const hexString = value.startsWith('0x') ? value.slice(2) : value
                        return new TextDecoder()
                            .decode(new Uint8Array(hexString.match(/.{1,2}/g).map(byte => parseInt(byte, 16))))
                            .replace(/\x00/g, '')

                    case 'bytes32':
                    case 'bytes':
                        return value.slice(2).toUpperCase()

                    case 'bool':
                        return value === '0x0' ? 'false' : 'true'

                    default:
                        return value
                }
            } catch (error) {
                console.error('Error decoding value:', error)
                return value
            }
        },
        [getAddressName]
    )
}

module.exports = {
    useFormatBytes,
    useFormatDateTime,
    useFormatTimestampShort,
    useFormatTimestamp,
    useFormatTransactionCount,
    useHexToString,
    useFormatNumber,
    useFormatHexValue
}
